import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoUser } from 'projects/app-core/src/auth/models/IAnyoUser';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { TherapyConcerns } from 'projects/app-core/src/models/TherapyConcerns';
import { TherapyVerticals } from 'projects/app-core/src/models/TherapyVerticals';
import {
  AnyoS3Buckets,
  FileUtilsService,
} from 'projects/app-core/src/service/fileService';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { MasterDataResponse } from '../../../models/masterDataResponse';
import {
  PartnerFilter,
  PartnerGridRequest,
} from '../../../models/partnerGridRequest';
import { PartnerTable } from '../../../models/partnerTable';
import { TherapyPlansAdminResponse } from '../../../models/TherapyPlansAdminResponse';
import {
  AddEditConcernMasterDataReq,
  AddEditVerticalMasterDataReq,
  MasterdataService,
} from '../../../services/masterdata.service';
import { PartnerService } from '../../../services/partner.service';
import { TherapyPlansService } from '../../../services/therapy-plans.service';

export interface TherapyPlansResponse {
  verticalName: string;
  verticalId: string;
  verticalVisible: boolean;
  concerns: {
    concernName: string;
    concernId: string;
    plans: TherapyPlansAdminResponse[];
    concernVisible: boolean;
  }[];
}

@Component({
  selector: 'app-therapy-plans-list',
  templateUrl: './therapy-plans-list.component.html',
  styleUrl: './therapy-plans-list.component.scss',
})
export class TherapyPlansListComponent extends AnyoComponent {
  unLinkPlan(verticalId: string, concernId: string, planId: string) {
    this.pageLoading = true;
    this.therapyPlansService
      .unLinkTherapyPlan(verticalId, concernId, planId)
      .subscribe({
        next: () => {
          this.tostService.showSuccess('Plan unlinked successfully');
          this.pageLoading = false;
          window.location.reload();
        },
        error: (error) => {
          this.tostService.showError(error.message);
          this.pageLoading = false;
        },
      });
  }
  selectedPlansFn($event: string[]) {
    this.selectedPlan = $event;
  }
  selectedConcernsFn($event: string[]) {
    this.selectedConcern = $event;
  }
  verticals: {
    id: string;
    name: string;
    isVisible: boolean;
  }[] = [];
  concerns: {
    verticalId: string[];
    id: string;
    name: string;
    isVisible: boolean;
  }[] = [];
  concernsFiltered: { verticalId: string[]; id: string; name: string }[] = [];
  selectedVertical: string | undefined;
  selectedConcern: string[] = [];
  selectedPlan: string[] = [];

  selectedVerticalFn(event: string) {
    this.selectedVertical = event;
    this.selectedConcern = [];
    this.selectedPlan = [];
    this.concernsFiltered = this.concerns.filter((concern) =>
      concern.verticalId.includes(event),
    );
  }
  navigateToCreatePlanPage() {
    this.router.navigate(['/therapy-plans/create']);
  }
  showLinkingModalFn() {
    this.showLinkingModal = true;
  }
  handleLinkingModalOk() {
    if (!this.selectedVertical) {
      this.tostService.showError('Please select a Vertical');
      return;
    }
    if (!this.selectedConcern.length) {
      this.tostService.showError('Please select atleast one Concern');
      return;
    }
    if (!this.selectedPlan.length) {
      this.tostService.showError('Please select a Plan');
      return;
    }
    this.loading = true;
    this.therapyPlansService
      .linkTherapyPlan(
        this.selectedVertical,
        this.selectedConcern,
        this.selectedPlan,
      )
      .subscribe({
        next: async () => {
          this.tostService.showSuccess('Plan linked successfully');
          this.showLinkingModal = false;
          this.loading = false;
          await this.getMasterData();
          await this.fetchAllPlans();
          this.selectedVertical = undefined;
          this.selectedConcern = [];
          this.selectedPlan = [];
          // window.location.reload();
        },
        error: (error) => {
          this.tostService.showError(error.message);
          this.loading = false;
        },
      });
  }
  handleLinkingModalCancel() {
    this.showLinkingModal = false;
  }
  loading: boolean = true;
  showLinkingModal: boolean = false;
  override async ready(user?: IAnyoUser | undefined): Promise<void> {
    if (user) {
      this.loading = true;
      // First get master data to have all verticals and concerns
      await this.getMasterData();
      await this.fetchAllPlans();
    } else {
      this.loading = false;
    }
    return;
  }

  async fetchAllPlans() {
    this.therapyPlansService.fetchAllPlans().subscribe({
      next: (response) => {
        this.activePlans = response.activePlans;
        const rawPlansMapped = response.plansMapped;

        // Create a complete map of all verticals including those without plans
        this.plansMapped = this.verticals.map((vertical) => {
          // Find if this vertical exists in the response
          const existingVertical = rawPlansMapped.find(
            (v) => v.verticalId === vertical.id,
          );

          // Get all concerns for this vertical
          const verticalConcerns = this.concerns.filter((concern) =>
            concern.verticalId.includes(vertical.id),
          );

          if (existingVertical) {
            // Add missing concerns to the existing vertical
            const existingConcernIds = existingVertical.concerns.map(
              (c) => c.concernId,
            );

            // Find concerns that don't exist in the response
            const missingConcerns = verticalConcerns.filter(
              (concern) => !existingConcernIds.includes(concern.id),
            );

            // Add missing concerns with empty plans array
            missingConcerns.forEach((concern) => {
              existingVertical.concerns.push({
                concernName: concern.name,
                concernId: concern.id,
                concernVisible: concern.isVisible,
                plans: [],
              });
            });

            return existingVertical;
          } else {
            // Create an empty vertical entry with all its concerns
            return {
              verticalName: vertical.name,
              verticalId: vertical.id,
              verticalVisible: vertical.isVisible,
              concerns: verticalConcerns.map((concern) => ({
                concernName: concern.name,
                concernId: concern.id,
                concernVisible: concern.isVisible,
                plans: [],
              })),
            };
          }
        });

        // Set up UI state arrays
        this.plansMapped.forEach((vertical, index) => {
          this.isVerticalOpen[index] = false;
          this.isConcernOpen[index] = [];
          vertical.concerns.forEach((concern, subIndex) => {
            this.isConcernOpen[index][subIndex] = false;
          });
        });

        this.loading = false;
      },
      error: (error) => {
        this.tostService.showError(error.message);
        this.loading = false;
      },
    });
  }

  masterData: MasterDataResponse = {} as MasterDataResponse;

  getMasterData() {
    return new Promise<void>((resolve) => {
      this.masterDataService.masterDataList().subscribe({
        next: (value) => {
          this.masterData = value;
          this.concerns = (value.therapyConcerns || []).map((value) => {
            return {
              id: value.m_id,
              name: value.data,
              verticalId: value.verticalId,
              isVisible: value.isVisible !== undefined ? value.isVisible : true,
            };
          });

          this.verticals = (value.therapyVerticals || []).map((value) => {
            return {
              id: value.m_id,
              name: value.data,
              isVisible: value.isVisible !== undefined ? value.isVisible : true,
            };
          });

          (value.therapistExpertLevel || []).forEach((value) => {
            this.expertLevelMap[value.m_id] = value.data;
          });

          this.expertLevelArray = Object.keys(this.expertLevelMap);
          resolve();
        },
        error: (err) => {
          this.tostService.showError(err.message);
          resolve();
        },
      });
    });
  }
  expertLevelArray: string[] = [];
  expertLevelMap: Record<string, string> = {};
  activePlans: TherapyPlansAdminResponse[] = [];
  plansMapped: TherapyPlansResponse[] = [];
  isVerticalOpen: boolean[] = [];
  isConcernOpen: boolean[][] = [];
  verticalOpen = false;
  isVisibleConcern = false;
  isConcernOkLoading = false;
  concernName = '';
  concernDescription = '';
  expertiseTagline = '';
  selectedVerticalId = '';
  constructor(
    auth: AuthService,
    private therapyPlansService: TherapyPlansService,
    private tostService: ToastService,
    private router: Router,
    private masterDataService: MasterdataService,
    private fileService: FileUtilsService,
    private partnerService: PartnerService,
  ) {
    super(auth);
  }

  partnerList: PartnerTable[] = [];
  partnersLoading = false;

  notApplicablePartnerIdsVertical: string[] = [];
  notApplicablePartnerIdsConcern: string[] = [];

  sessionIntervalsVertical: {
    key: string;
    value: number | null | undefined | string;
  }[] = [];
  sessionIntervalsConcern: {
    key: string;
    value: number | null | undefined | string;
  }[] = [];

  loadPartnerList(): void {
    this.partnersLoading = true;
    const gridRequest: PartnerGridRequest = {
      page: 1,
      pageSize: 500,
      export: false,
      filters: {
        [PartnerFilter.CITY]: undefined,
        [PartnerFilter.STATE]: undefined,
        [PartnerFilter.TESTING]: undefined,
      },
      sort: undefined,
      searchQuery: undefined,
      dateFilters: undefined,
    };
    this.partnerService.partnerList(gridRequest).subscribe({
      next: (res) => {
        this.partnerList = res.data || [];
        this.partnersLoading = false;
      },
      error: () => {
        this.partnersLoading = false;
        this.tostService.showError('Failed to load partners');
      },
    });
  }

  getUsedPartnerKeysVertical(): string[] {
    return this.sessionIntervalsVertical
      .map((r) => r.key)
      .filter((k) => k && k !== 'default');
  }

  getUsedPartnerKeysConcern(): string[] {
    return this.sessionIntervalsConcern
      .map((r) => r.key)
      .filter((k) => k && k !== 'default');
  }

  /** Keys used in other rows (excluding current row key) so current row's partner stays in options for prefilled edit */
  getUsedPartnerKeysVerticalExcluding(currentRowKey: string): string[] {
    return this.sessionIntervalsVertical
      .map((r) => r.key)
      .filter((k) => k && k !== 'default' && k !== currentRowKey);
  }

  getUsedPartnerKeysConcernExcluding(currentRowKey: string): string[] {
    return this.sessionIntervalsConcern
      .map((r) => r.key)
      .filter((k) => k && k !== 'default' && k !== currentRowKey);
  }

  /** Partners available for session-interval rows: exclude not-applicable list and other rows' keys */
  getPartnersForSessionIntervalsVertical(
    excludeKeys: string[],
  ): PartnerTable[] {
    return this.partnerList.filter(
      (p) =>
        !this.notApplicablePartnerIdsVertical.includes(p.partnerId) &&
        !excludeKeys.includes(p.partnerId),
    );
  }

  getPartnersForSessionIntervalsConcern(excludeKeys: string[]): PartnerTable[] {
    return this.partnerList.filter(
      (p) =>
        !this.notApplicablePartnerIdsConcern.includes(p.partnerId) &&
        !excludeKeys.includes(p.partnerId),
    );
  }

  addSessionIntervalRowVertical(): void {
    this.sessionIntervalsVertical.push({ key: '', value: undefined });
  }

  removeSessionIntervalRowVertical(index: number): void {
    if (this.sessionIntervalsVertical[index].key === 'default') return;
    this.sessionIntervalsVertical.splice(index, 1);
  }

  addSessionIntervalRowConcern(): void {
    this.sessionIntervalsConcern.push({ key: '', value: undefined });
  }

  removeSessionIntervalRowConcern(index: number): void {
    if (this.sessionIntervalsConcern[index].key === 'default') return;
    this.sessionIntervalsConcern.splice(index, 1);
  }

  /** Build sessionIntervals for API. Use null for empty so the key is preserved in JSON (undefined is omitted). For keys other than default, omit if value is 0 or empty. */
  buildSessionIntervalsRecord(
    rows: { key: string; value: number | null | undefined | string }[],
  ): Record<string, number | null> | undefined {
    const rec: Record<string, number | null> = {};
    for (const row of rows) {
      if (!row.key) continue;
      const raw = row.value;
      const value: number | null =
        typeof raw === 'number' && !Number.isNaN(raw)
          ? raw
          : raw === '' || raw === undefined || raw === null
            ? null
            : Number(raw);
      const normalized: number | null =
        typeof value === 'number' && !Number.isNaN(value) ? value : null;
      const isDefault = row.key.toLowerCase() === 'default';
      if (isDefault) {
        rec[row.key] = normalized;
      } else if (normalized !== null) {
        rec[row.key] = normalized;
      }
    }
    return Object.keys(rec).length ? rec : undefined;
  }
  toggleVertical(index: number, id: string) {
    this.isVerticalOpen[index] = !this.isVerticalOpen[index];
    const someTrue = this.isVerticalOpen.some((data) => data);
    if (!someTrue) {
      this.verticalOpen = false;
    } else {
      this.verticalOpen = true;
    }
    this.selectedVerticalId = id;
  }

  toggleConcern(verticalIndex: number, concernIndex: number) {
    this.isConcernOpen[verticalIndex][concernIndex] =
      !this.isConcernOpen[verticalIndex][concernIndex];
  }

  verticalName = '';
  verticalImage!: File | null;
  primaryColor = '';
  secondaryColor = '';
  verticalDescription = '';
  isHealthProgram = false;
  verticalImageUrl = '';
  sessionIntervalVertical = 0;
  sessionIntervalConcern = 0;
  userSelectSessionDuration = false;
  userSelectSessionDurationConcern = false;

  isVisible = false;
  isOkLoading = false;
  isEditVertical = false;
  isEditConcern = false;

  showAddVertical(): void {
    this.isVisible = true;
    this.verticalName = '';
    this.verticalImage = null;
    this.primaryColor = '';
    this.secondaryColor = '';
    this.verticalDescription = '';
    this.isHealthProgram = false;
    this.verticalImageUrl = '';
    this.isEditVertical = false;
    this.sessionIntervalVertical = 0;
    this.userSelectSessionDuration = false;
    this.notApplicablePartnerIdsVertical = [];
    this.sessionIntervalsVertical = [{ key: 'default', value: undefined }];
    this.loadPartnerList();
  }

  concernImage!: File | null;
  concernImageUrl = '';

  showAddConcern(verticalId: string): void {
    this.isVisibleConcern = true;
    this.concernName = '';
    this.concernDescription = '';
    this.expertiseTagline = '';
    this.concernImage = null;
    this.concernImageUrl = '';
    this.selectedVerticalId = verticalId;
    this.isEditConcern = false;
    this.sessionIntervalConcern = 0;
    this.userSelectSessionDurationConcern = false;
    this.notApplicablePartnerIdsConcern = [];
    this.sessionIntervalsConcern = [{ key: 'default', value: undefined }];
    this.loadPartnerList();
  }

  selectedVerticalObj!: TherapyVerticals;
  selectedConcernObj!: TherapyConcerns;

  showUpdateVertical(verticalId: string) {
    this.pageLoading = true;
    const vertical = this.masterData.therapyVerticals.find(
      (v) => v.m_id === verticalId,
    );
    if (!vertical) {
      this.tostService.showError('Vertical not found');
      this.pageLoading = false;
      return;
    }
    this.selectedVerticalObj = vertical as TherapyVerticals;
    this.verticalName = vertical.data;
    this.verticalImageUrl = vertical.image || '';
    this.primaryColor = vertical.primaryColor || '';
    this.secondaryColor = vertical.secondaryColor || '';
    this.verticalDescription = vertical.description || '';
    this.isHealthProgram = vertical.isHealthProgram || false;
    this.selectedVerticalId = vertical.m_id;
    this.sessionIntervalVertical = vertical.sessionInterval || 0;
    this.userSelectSessionDuration =
      vertical.userSelectSessionDuration || false;
    this.notApplicablePartnerIdsVertical =
      vertical.notApplicablePartner?.slice() ?? [];
    const si = vertical.sessionIntervals;
    if (si && Object.keys(si).length) {
      this.sessionIntervalsVertical = Object.entries(si).map(([k, v]) => ({
        key: k,
        value: v ?? undefined,
      }));
      if (!this.sessionIntervalsVertical.some((r) => r.key === 'default')) {
        this.sessionIntervalsVertical.unshift({
          key: 'default',
          value: vertical.sessionInterval ?? undefined,
        });
      }
    } else {
      this.sessionIntervalsVertical = [
        { key: 'default', value: vertical.sessionInterval ?? undefined },
      ];
    }
    this.isEditVertical = true;
    this.isVerticalVisible =
      vertical.isVisible !== undefined ? vertical.isVisible : true;
    this.isVisible = true;
    this.pageLoading = false;
    this.loadPartnerList();
  }

  selectedConcernId = '';

  showUpdateConcern(concernId: string) {
    this.pageLoading = true;
    const concern = this.masterData.therapyConcerns.find(
      (c) => c.m_id === concernId,
    );
    if (!concern) {
      this.tostService.showError('Concern not found');
      this.pageLoading = false;
      return;
    }
    this.selectedConcernObj = concern as TherapyConcerns;
    this.sessionIntervalConcern = concern.sessionInterval || 0;
    this.concernName = concern.data;
    this.concernImage = null;
    this.concernImageUrl = concern.image || '';
    this.concernDescription = concern.description || '';
    this.expertiseTagline = concern.expertiseTagline || '';
    this.selectedConcernId = concern.m_id;
    this.isConcernVisible =
      concern.isVisible !== undefined ? concern.isVisible : true;
    this.notApplicablePartnerIdsConcern =
      concern.notApplicablePartner?.slice() ?? [];
    const si = concern.sessionIntervals;
    if (si && Object.keys(si).length) {
      this.sessionIntervalsConcern = Object.entries(si).map(([k, v]) => ({
        key: k,
        value: v ?? undefined,
      }));
      if (!this.sessionIntervalsConcern.some((r) => r.key === 'default')) {
        this.sessionIntervalsConcern.unshift({
          key: 'default',
          value: concern.sessionInterval ?? undefined,
        });
      }
    } else {
      this.sessionIntervalsConcern = [
        { key: 'default', value: concern.sessionInterval ?? undefined },
      ];
    }
    this.isEditConcern = true;
    this.isVisibleConcern = true;
    this.pageLoading = false;
    this.userSelectSessionDurationConcern =
      concern.userSelectSessionDuration || false;
    this.loadPartnerList();
  }

  handleCancel(): void {
    this.isVisible = false;
  }

  handleConcernCancel() {
    this.isVisibleConcern = false;
  }

  uploadVerticalImage($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    const file = inputElement.files?.[0];
    if (file) {
      this.verticalImage = file;
    }
  }

  uploadConcernImage($event: Event) {
    const inputElement = $event.target as HTMLInputElement;
    const file = inputElement.files?.[0];
    if (file) {
      this.concernImage = file;
    }
  }

  isVerticalVisible: boolean = true;

  async addNewVertical() {
    this.isOkLoading = true;
    const req: AddEditVerticalMasterDataReq = {
      verticalName: this.verticalName,
      image: this.isEditVertical ? this.verticalImageUrl : '',
      primaryColor: this.primaryColor,
      secondaryColor: this.secondaryColor,
      description: this.verticalDescription,
      isHealthProgram: this.isHealthProgram,
      isVisible: this.isVerticalVisible,
      sessionInterval: this.sessionIntervalVertical,
      userSelectSessionDuration: this.userSelectSessionDuration,
      notApplicablePartner: [],
    };
    if (this.isEditVertical) {
      req.verticalId = this.selectedVerticalId;
    }
    if (this.notApplicablePartnerIdsVertical.length) {
      req.notApplicablePartner = [...this.notApplicablePartnerIdsVertical];
    }
    const sessionIntervalsRec = this.buildSessionIntervalsRecord(
      this.sessionIntervalsVertical,
    );
    if (sessionIntervalsRec) req.sessionIntervals = sessionIntervalsRec;
    if (this.verticalImage) {
      const uuid = uuidv4();
      const uuidWithoutHyphens = uuid.replace(/-/g, '');
      const extension = this.verticalImage.name.split('.').pop();
      const filePath = `vertical/${uuidWithoutHyphens}.${extension}`;
      const uploadUrl = await lastValueFrom(
        this.fileService.generateUploadUrl(
          AnyoS3Buckets.CDN_BUCKET,
          filePath,
          this.verticalImage.type,
        ),
      );
      this.tostService.showSuccess(`Uploading vertical image`);
      await lastValueFrom(
        this.fileService.uploadFile(this.verticalImage, uploadUrl.url),
      );
      this.tostService.showSuccess(`Uploading vertical image completed`);
      req.image = `https://cdn.anyo.app/${filePath}`;
    }
    this.masterDataService.addVertical(req).subscribe({
      next: async () => {
        this.tostService.showSuccess(
          'Vertical Successfully ' +
            (this.isEditVertical ? 'Updated!!' : 'Added!!'),
        );
        this.isVisible = false;
        this.isOkLoading = false;
        await this.getMasterData();
        await this.fetchAllPlans();
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.tostService.showAnyoErrorToast(errorBody.description);
        this.isOkLoading = false;
      },
    });
  }

  isConcernVisible: boolean = true;
  async addNewConcern() {
    this.isConcernOkLoading = true;
    const editReq: AddEditConcernMasterDataReq = {
      concernName: this.concernName,
      description: this.concernDescription,
      verticalId: this.selectedVerticalId,
      image: this.isEditConcern ? this.concernImageUrl : '',
      isVisible: this.isConcernVisible,
      sessionInterval: this.sessionIntervalConcern,
      userSelectSessionDuration: this.userSelectSessionDurationConcern,
      notApplicablePartner: [],
    };
    const trimmedTagline = this.expertiseTagline?.trim();
    if (trimmedTagline) {
      editReq.expertiseTagline = trimmedTagline;
    }
    if (this.isEditConcern) {
      editReq.concernId = this.selectedConcernId;
    }
    if (this.notApplicablePartnerIdsConcern.length) {
      editReq.notApplicablePartner = [...this.notApplicablePartnerIdsConcern];
    }
    const sessionIntervalsRec = this.buildSessionIntervalsRecord(
      this.sessionIntervalsConcern,
    );
    if (sessionIntervalsRec) editReq.sessionIntervals = sessionIntervalsRec;
    if (this.concernImage) {
      const uuid = uuidv4();
      const uuidWithoutHyphens = uuid.replace(/-/g, '');
      const extension = this.concernImage.name.split('.').pop();
      const filePath = `master-data/therapyconcerns/${uuidWithoutHyphens}.${extension}`;
      const uploadUrl = await lastValueFrom(
        this.fileService.generateUploadUrl(
          AnyoS3Buckets.CDN_BUCKET,
          filePath,
          this.concernImage.type,
        ),
      );
      this.tostService.showSuccess(`Uploading concern image`);
      await lastValueFrom(
        this.fileService.uploadFile(this.concernImage, uploadUrl.url),
      );
      this.tostService.showSuccess(`Uploading concern image completed`);
      editReq.image = `https://cdn.anyo.app/${filePath}`;
    }
    this.masterDataService.addConcern(editReq).subscribe({
      next: async () => {
        this.tostService.showSuccess(
          'Concern Successfully ' +
            (this.isEditConcern ? 'Updated!!' : 'Added!!'),
        );
        this.isVisibleConcern = false;
        this.isConcernOkLoading = false;
        await this.getMasterData();
        await this.fetchAllPlans();
      },
      error: (err) => {
        const errorBody = err.error as IAnyoError;
        this.tostService.showAnyoErrorToast(errorBody.description);
        this.isConcernOkLoading = false;
      },
    });
  }
}
